import { fileKindForName } from "@/features/caseFiles/fileKind";

import type {
  AgentAttachment,
  AttachmentFilePart,
  ComposerAttachment
} from "./types";

export const ATTACHMENT_URL_PREFIX = "attachment:";
/** The server's limits (`agent_attachment_service`). */
export const MAX_ATTACHMENTS_PER_MESSAGE = 10;
export const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;

/** What the agents can read, by extension (mirrors the server's list). */
export const ATTACHMENT_EXTENSIONS = [
  ".pdf",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".heic",
  ".heif",
  ".tif",
  ".tiff",
  ".docx",
  ".doc",
  ".rtf",
  ".odt",
  ".xlsx",
  ".xls",
  ".csv",
  ".pptx",
  ".ppt",
  ".txt",
  ".md"
] as const;

/** The file input's `accept`. */
export const ATTACHMENT_ACCEPT = ATTACHMENT_EXTENSIONS.join(",");

const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf(".");
  return dot < 0 ? "" : name.slice(dot).toLowerCase();
};

/** Why a picked file cannot be attached, or null when it can. */
export const attachmentProblem = (file: {
  name: string;
  size: number;
}): string | null => {
  if (
    !(ATTACHMENT_EXTENSIONS as readonly string[]).includes(
      extensionOf(file.name)
    )
  )
    return `${file.name} isn't a file the agent can read`;
  if (file.size <= 0) return `${file.name} is empty`;
  if (file.size > MAX_ATTACHMENT_BYTES)
    return `${file.name} is larger than 50 MB`;
  return null;
};

export const attachmentIdOf = (url: string): string | null =>
  url.startsWith(ATTACHMENT_URL_PREFIX)
    ? url.slice(ATTACHMENT_URL_PREFIX.length) || null
    : null;

/** The message part for an uploaded attachment. */
export const toFilePart = (a: AgentAttachment): AttachmentFilePart => ({
  type: "file",
  mediaType: a.content_type,
  filename: a.filename,
  url: a.url || `${ATTACHMENT_URL_PREFIX}${a.id}`,
  providerMetadata: {
    caseblink: {
      sizeBytes: a.size_bytes,
      ...(a.page_count ? { pageCount: a.page_count } : {})
    }
  }
});

export type AttachmentFileMeta = { sizeBytes?: number; pageCount?: number };

const countOf = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isInteger(value) && value >= 0
    ? value
    : undefined;

/** Size and page count stamped on a part by `toFilePart`; older parts have neither. */
export const fileMetaOf = (part: AttachmentFilePart): AttachmentFileMeta => {
  const meta: unknown = part.providerMetadata?.caseblink;
  if (!meta || typeof meta !== "object") return {};
  const { sizeBytes, pageCount } = meta as Record<string, unknown>;
  return { sizeBytes: countOf(sizeBytes), pageCount: countOf(pageCount) };
};

/** A message's attachment parts (`file` parts pointing at `attachment:`). */
export const attachmentPartsOf = (
  parts: ReadonlyArray<{ type: string }>
): AttachmentFilePart[] =>
  parts.filter(
    (p): p is AttachmentFilePart =>
      p.type === "file" &&
      typeof (p as AttachmentFilePart).url === "string" &&
      (p as AttachmentFilePart).url.startsWith(ATTACHMENT_URL_PREFIX)
  );

/** The parts of an outgoing message: its text (if any), then its files. */
export const messageParts = (
  text: string,
  files: readonly AttachmentFilePart[] = []
): Array<{ type: "text"; text: string } | AttachmentFilePart> => [
  ...(text ? [{ type: "text" as const, text }] : []),
  ...files
];

export const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;
};

/** A card's second line: "PDF · 3 pages · 284 KB", from whatever is known. */
export const fileMetaLine = (
  badge: string,
  { sizeBytes, pageCount }: AttachmentFileMeta
): string =>
  [
    badge,
    pageCount ? `${pageCount} page${pageCount === 1 ? "" : "s"}` : null,
    sizeBytes ? formatBytes(sizeBytes) : null
  ]
    .filter(Boolean)
    .join(" · ");

/** "PDF", "DOCX"…, or the kind's name when the file has no extension. */
export const attachmentTypeLabel = (
  filename: string,
  mediaType?: string
): string => {
  const kind = fileKindForName(filename, mediaType);
  return kind.badge || kind.label;
};

/** From this many files the composer shows a summary tray instead of cards. */
export const ATTACHMENT_TRAY_THRESHOLD = 3;

/** Composer files that share a name and differ only by extension. */
export type ComposerAttachmentGroup = {
  key: string;
  /** The shared name, without the extension. */
  name: string;
  items: ComposerAttachment[];
};

const stemOf = (filename: string): string => {
  const dot = filename.lastIndexOf(".");
  return dot > 0 ? filename.slice(0, dot) : filename;
};

const hasFailed = (group: ComposerAttachmentGroup): boolean =>
  group.items.some((i) => i.status === "failed");

/** One group per name (the DOCX and PDF of one exhibit together), failed groups first. */
export const groupAttachmentsByStem = (
  items: readonly ComposerAttachment[]
): ComposerAttachmentGroup[] => {
  const byStem = new Map<string, ComposerAttachmentGroup>();
  for (const item of items) {
    const stem = stemOf(item.filename);
    const group = byStem.get(stem);
    if (group) group.items.push(item);
    else byStem.set(stem, { key: item.localId, name: stem, items: [item] });
  }
  const groups = [...byStem.values()];
  return [...groups.filter(hasFailed), ...groups.filter((g) => !hasFailed(g))];
};

/** "5 DOCX · 4 PDF": file counts per type, most common first. */
export const attachmentTypeMix = (
  items: readonly ComposerAttachment[]
): string => {
  const counts = new Map<string, number>();
  for (const item of items) {
    const label = attachmentTypeLabel(item.filename, item.contentType);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([label, n]) => `${n} ${label}`)
    .join(" · ");
};
